import { useState } from 'react';
import { subscriptionUpgradeService, BulkUpgradeRequest, BulkUpgradePreview } from '@/services/subscriptionUpgradeService';
import { SubscriptionRecord } from '@/hooks/useSubscriptionData';
import { useToast } from '@/hooks/use-toast';
import { canSelectSubscriptionForUpgrade } from '@/utils/subscriptionUpgradeHelpers';

export interface SelectedSubscriptionForUpgrade {
  id: string;
  stripe_subscription_id: string;
  stripe_customer_id?: string;
  customer_email?: string;
  current_amount: number; 
  subscription_interval?: string; 
  current_period_end?: string;
  metadata?: any;
}

export const useSubscriptionBulkUpgrade = () => {
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<Set<string>>(new Set());
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<BulkUpgradePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Toggle subscription selection
   */
  const toggleSubscriptionSelection = (subscriptionId: string, subscription?: SubscriptionRecord) => {
    if (subscription) {
      const canSelect = canSelectSubscriptionForUpgrade(
        subscription.upgrade_status,
        subscription.has_pending_upgrade
      );
      
      if (!canSelect && !selectedSubscriptions.has(subscriptionId)) {
        return;
      }
    }
    
    const newSelected = new Set(selectedSubscriptions);
    if (newSelected.has(subscriptionId)) {
      newSelected.delete(subscriptionId);
    } else {
      newSelected.add(subscriptionId);
    }
    setSelectedSubscriptions(newSelected);
  };

  /**
   * Select all subscriptions
   */
  const selectAllSubscriptions = (subscriptions: SubscriptionRecord[]) => {
    const selectableSubscriptions = subscriptions.filter(sub => 
      canSelectSubscriptionForUpgrade(sub.upgrade_status, sub.has_pending_upgrade)
    );
    setSelectedSubscriptions(new Set(selectableSubscriptions.map(sub => sub.id)));
  };

  /**
   * Clear selection
   */
  const clearSelection = () => {
    setSelectedSubscriptions(new Set());
  };

  /**
   * Extract subscription amount from Stripe subscription data
   * This is a helper to get the current amount from various possible fields
   */
  const extractSubscriptionAmount = (subscription: any, fallbackAmount: number = 0): number => {
    console.log('[BulkUpgrade] Extracting amount for subscription:', subscription.id);
    console.log('[BulkUpgrade] Subscription structure check:', {
      hasItems: !!subscription.items,
      itemsType: Array.isArray(subscription.items) ? 'array' : typeof subscription.items,
      itemsLength: Array.isArray(subscription.items) ? subscription.items.length : 'N/A',
      hasLatestInvoice: !!subscription.latest_invoice,
      hasLatestInvoiceObject: typeof subscription.latest_invoice === 'object'
    });
    
    // PRIORITY 1: Try items array structure (from transformSubscription)
    if (Array.isArray(subscription.items) && subscription.items.length > 0) {
      const firstItem = subscription.items[0];
      console.log('[BulkUpgrade] First item structure:', firstItem);
      
      if (firstItem?.price?.unit_amount) {
        const amount = firstItem.price.unit_amount / 100;
        console.log('[BulkUpgrade] Found amount in items[0].price.unit_amount:', amount);
        return amount;
      }
    }
    
    // PRIORITY 2: Try items.data structure (original Stripe format)
    if (subscription.items?.data?.[0]?.price?.unit_amount) {
      const amount = subscription.items.data[0].price.unit_amount / 100;
      console.log('[BulkUpgrade] Found amount in items.data[0].price.unit_amount:', amount);
      return amount;
    }
    
    // PRIORITY 3: Try latest_invoice from transformed structure
    if (subscription.latest_invoice && typeof subscription.latest_invoice === 'object') {
      if (subscription.latest_invoice.amount_due) {
        const amount = subscription.latest_invoice.amount_due / 100;
        console.log('[BulkUpgrade] Found amount in latest_invoice.amount_due:', amount);
        return amount;
      }
      
      if (subscription.latest_invoice.amount_paid) {
        const amount = subscription.latest_invoice.amount_paid / 100;
        console.log('[BulkUpgrade] Found amount in latest_invoice.amount_paid:', amount);
        return amount;
      }
    }
    
    // PRIORITY 4: Try original Stripe structures for backward compatibility
    if (subscription.items?.[0]?.price?.unit_amount) {
      const amount = subscription.items[0].price.unit_amount / 100;
      console.log('[BulkUpgrade] Found amount in items[0].price.unit_amount (legacy):', amount);
      return amount;
    }
    
    if (subscription.latest_invoice?.amount_due) {
      const amount = subscription.latest_invoice.amount_due / 100;
      console.log('[BulkUpgrade] Found amount in latest_invoice.amount_due (legacy):', amount);
      return amount;
    }
    
    if (subscription.plan?.amount) {
      const amount = subscription.plan.amount / 100;
      console.log('[BulkUpgrade] Found amount in plan.amount:', amount);
      return amount;
    }
    
    // PRIORITY 5: Try latest_invoice total
    if (subscription.latest_invoice?.total) {
      const amount = subscription.latest_invoice.total / 100;
      console.log('[BulkUpgrade] Found amount in latest_invoice.total:', amount);
      return amount;
    }
    
    // PRIORITY 6: Try to extract from quantity * unit_amount pattern
    if (subscription.items?.data?.[0]?.quantity && subscription.items?.data?.[0]?.price?.unit_amount) {
      const amount = (subscription.items.data[0].quantity * subscription.items.data[0].price.unit_amount) / 100;
      console.log('[BulkUpgrade] Found amount via quantity calculation:', amount);
      return amount;
    }
    
    // PRIORITY 7: Try different nested structures that might contain pricing
    if (subscription.price?.unit_amount) {
      const amount = subscription.price.unit_amount / 100;
      console.log('[BulkUpgrade] Found amount in price.unit_amount:', amount);
      return amount;
    }
    
    // PRIORITY 8: Try current period amount if available
    if (subscription.current_period_amount) {
      const amount = subscription.current_period_amount / 100;
      console.log('[BulkUpgrade] Found amount in current_period_amount:', amount);
      return amount;
    }
    
    // If we have a fallback amount (e.g., from manual entry), use it
    if (fallbackAmount > 0) {
      console.log('[BulkUpgrade] Using provided fallback amount:', fallbackAmount);
      return fallbackAmount;
    }
    
    // Default fallback - you might want to handle this differently
    console.warn('[BulkUpgrade] Could not extract amount for subscription:', subscription.id);
    console.warn('[BulkUpgrade] Available top-level keys:', Object.keys(subscription));
    
    if (subscription.items) {
      console.warn('[BulkUpgrade] Items structure type:', typeof subscription.items);
      if (Array.isArray(subscription.items)) {
        console.warn('[BulkUpgrade] Items array length:', subscription.items.length);
        if (subscription.items.length > 0) {
          console.warn('[BulkUpgrade] First item keys:', Object.keys(subscription.items[0]));
          console.warn('[BulkUpgrade] First item price:', subscription.items[0].price);
        }
      } else {
        console.warn('[BulkUpgrade] Items object keys:', Object.keys(subscription.items));
      }
    }
    
    if (subscription.latest_invoice) {
      console.warn('[BulkUpgrade] Latest invoice type:', typeof subscription.latest_invoice);
      if (typeof subscription.latest_invoice === 'object') {
        console.warn('[BulkUpgrade] Latest invoice keys:', Object.keys(subscription.latest_invoice));
      }
    }
    
    // Return a reasonable default for testing (you can change this behavior)
    console.log('[BulkUpgrade] Using default amount of $10 for testing purposes');
    return 10; // $10 default for testing - you can change this behavior
  };

  /**
   * Extract subscription interval from Stripe subscription data
   */
  const extractSubscriptionInterval = (subscription: any): string => {
    console.log('[BulkUpgrade] Extracting interval for subscription:', subscription.id);

    // Log important structure for debugging
    console.log('[BulkUpgrade] Subscription structure check for interval:', {
      hasItems: !!subscription.items,
      itemsType: Array.isArray(subscription.items) ? 'array' : typeof subscription.items,
      itemsLength: Array.isArray(subscription.items) ? subscription.items.length : 
                  (subscription.items?.data ? subscription.items.data.length : 'N/A'),
      hasPlan: !!subscription.plan,
      hasPrice: !!subscription.price
    });
    
    // PRIORITY 1: Check items array structure
    if (Array.isArray(subscription.items) && subscription.items.length > 0) {
      const firstItem = subscription.items[0];
      
      if (firstItem?.price?.recurring?.interval) {
        const interval = firstItem.price.recurring.interval;
        console.log('[BulkUpgrade] ✅ Found interval in items[0].price.recurring.interval:', interval);
        return interval;
      }
      
      // Sometimes price might be directly on the item
      if (firstItem?.recurring?.interval) {
        const interval = firstItem.recurring.interval;
        console.log('[BulkUpgrade] ✅ Found interval in items[0].recurring.interval:', interval);
        return interval;
      }
    }
    
    // PRIORITY 2: Check items.data structure (standard Stripe API format)
    if (subscription.items?.data?.[0]?.price?.recurring?.interval) {
      const interval = subscription.items.data[0].price.recurring.interval;
      console.log('[BulkUpgrade] ✅ Found interval in items.data[0].price.recurring.interval:', interval);
      return interval;
    }
    
    // PRIORITY 3: Check plan (older Stripe API format)
    if (subscription.plan?.interval) {
      const interval = subscription.plan.interval;
      console.log('[BulkUpgrade] ✅ Found interval in plan.interval:', interval);
      return interval;
    }
    
    // PRIORITY 4: Check price directly
    if (subscription.price?.recurring?.interval) {
      const interval = subscription.price.recurring.interval;
      console.log('[BulkUpgrade] ✅ Found interval in price.recurring.interval:', interval);
      return interval;
    }
    
    // PRIORITY 5: Check metadata
    if (subscription.metadata?.billing_interval) {
      const interval = subscription.metadata.billing_interval;
      console.log('[BulkUpgrade] ✅ Found interval in metadata.billing_interval:', interval);
      return interval;
    }
    
    // PRIORITY 6: Check billing_cycle_anchor and cancel_at to determine if yearly
    if (subscription.billing_cycle_anchor && subscription.cancel_at) {
      const anchorDate = new Date(subscription.billing_cycle_anchor * 1000);
      const cancelDate = new Date(subscription.cancel_at * 1000);
      const diffMonths = (cancelDate.getFullYear() - anchorDate.getFullYear()) * 12 + 
                         (cancelDate.getMonth() - anchorDate.getMonth());
      
      if (diffMonths >= 12) {
        console.log('[BulkUpgrade] ✅ Determined yearly interval from billing_cycle_anchor and cancel_at');
        return 'year';
      } else if (diffMonths > 1) {
        console.log('[BulkUpgrade] ✅ Determined multi-month interval from billing_cycle_anchor and cancel_at');
        return 'month';
      }
    }
    
    // PRIORITY 7: Check current_period_start and current_period_end to estimate interval
    if (subscription.current_period_start && subscription.current_period_end) {
      const startDate = new Date(subscription.current_period_start * 1000);
      const endDate = new Date(subscription.current_period_end * 1000);
      const diffDays = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays >= 365) {
        console.log('[BulkUpgrade] ✅ Determined yearly interval from period length:', diffDays, 'days');
        return 'year';
      } else if (diffDays >= 60) {
        console.log('[BulkUpgrade] ✅ Determined quarterly interval from period length:', diffDays, 'days');
        return 'quarter';
      } else if (diffDays >= 28) {
        console.log('[BulkUpgrade] ✅ Determined monthly interval from period length:', diffDays, 'days');
        return 'month';
      } else if (diffDays >= 6) {
        console.log('[BulkUpgrade] ✅ Determined weekly interval from period length:', diffDays, 'days');
        return 'week';
      } else {
        console.log('[BulkUpgrade] ✅ Determined daily interval from period length:', diffDays, 'days');
        return 'day';
      }
    }
    
    // Log detailed debugging info when interval not found
    console.warn('[BulkUpgrade] ❌ No interval found, defaulting to "month"');
    console.warn('[BulkUpgrade] Available top-level keys:', Object.keys(subscription));
    
    if (subscription.items) {
      console.warn('[BulkUpgrade] Items structure debug:');
      if (Array.isArray(subscription.items)) {
        console.warn('[BulkUpgrade] - Items is an array with length:', subscription.items.length);
        if (subscription.items.length > 0) {
          console.warn('[BulkUpgrade] - First item keys:', Object.keys(subscription.items[0]));
          if (subscription.items[0].price) {
            console.warn('[BulkUpgrade] - First item price keys:', Object.keys(subscription.items[0].price));
            if (subscription.items[0].price.recurring) {
              console.warn('[BulkUpgrade] - First item price.recurring keys:', Object.keys(subscription.items[0].price.recurring));
            }
          }
        }
      } else if (subscription.items.data) {
        console.warn('[BulkUpgrade] - Items has data array with length:', subscription.items.data.length);
        if (subscription.items.data.length > 0) {
          console.warn('[BulkUpgrade] - First data item keys:', Object.keys(subscription.items.data[0]));
          if (subscription.items.data[0].price) {
            console.warn('[BulkUpgrade] - First data item price keys:', Object.keys(subscription.items.data[0].price));
          }
        }
      }
    }
    
    if (subscription.plan) {
      console.warn('[BulkUpgrade] Plan keys:', Object.keys(subscription.plan));
    }
    
    // Default to monthly if not found
    return 'month';
  };
  const prepareSubscriptionsForUpgrade = (
    subscriptions: SubscriptionRecord[], 
    allStripeData: any[]
  ): SelectedSubscriptionForUpgrade[] => {
    console.log('[BulkUpgrade] Preparing subscriptions for upgrade');
    console.log('[BulkUpgrade] Selected subscription IDs:', Array.from(selectedSubscriptions));
    console.log('[BulkUpgrade] Available subscriptions:', subscriptions.length);
    console.log('[BulkUpgrade] Available Stripe data entries:', allStripeData.length);
    
    return subscriptions
      .filter(sub => selectedSubscriptions.has(sub.id))
      .map(sub => {
        console.log('[BulkUpgrade] Processing subscription:', sub.id);
        
        // Find corresponding Stripe data
        // Try to match by subscription ID (sub.id or sub.stripe_subscription_id)
        const stripeData = allStripeData.find(stripe => 
          stripe.id === sub.stripe_subscription_id || 
          stripe.id === sub.id ||
          stripe.stripe_subscription_id === sub.id
        );
        
        if (!stripeData) {
          console.warn('[BulkUpgrade] No Stripe data found for subscription:', sub.id);
          console.warn('[BulkUpgrade] Looking for:', sub.stripe_subscription_id || sub.id);
          console.warn('[BulkUpgrade] Available Stripe IDs:', allStripeData.map(s => s.id));
        } else {
          console.log('[BulkUpgrade] Found Stripe data for subscription:', sub.id);
        }
        
        // First try to extract amount from Stripe data
        let amount = 0;
        let interval = 'month'; // Default interval
        
        if (stripeData) {
          amount = extractSubscriptionAmount(stripeData, 0);
          interval = extractSubscriptionInterval(stripeData);
        }
        
        // If no amount found in Stripe data, try using the subscription record itself
        if (amount === 0 && sub) {
          console.log('[BulkUpgrade] Trying to extract amount from subscription record');
          amount = extractSubscriptionAmount(sub, 0);
          
          // Try to extract interval from subscription record if not already found
          if (interval === 'month') {
            interval = extractSubscriptionInterval(sub);
          }
        }
        
        // As a last resort, try to find amount in the original allStripeData array
        if (amount === 0) {
          console.log('[BulkUpgrade] Trying to extract amount from any matching Stripe data');
          const alternativeStripeData = allStripeData.find(stripe => 
            stripe.stripe_subscription_id === sub.id ||
            stripe.stripe_subscription_id === sub.stripe_subscription_id
          );
          if (alternativeStripeData) {
            amount = extractSubscriptionAmount(alternativeStripeData, 0);
          }
        }
        
        const result = {
          id: sub.id,
          stripe_subscription_id: sub.stripe_subscription_id || sub.id,
          stripe_customer_id: stripeData?.customer?.id || stripeData?.customer || stripeData?.customerId || '',
          customer_email: sub.customer_email || stripeData?.customer?.email || stripeData?.customerEmail || '',
          current_amount: amount,
          subscription_interval: interval,
          current_period_end: sub.current_period_end,
          metadata: {
            subscription_record: sub,
            stripe_data: stripeData,
            keepmore_subscription: sub,
            subscription_interval: interval
          }
        };
        
        console.log('[BulkUpgrade] Prepared subscription result:', {
          id: result.id,
          amount: result.current_amount,
          email: result.customer_email,
          hasStripeData: !!stripeData
        });
        
        return result;
      })
      .filter(sub => {
        if (sub.current_amount <= 0) {
          console.warn('[BulkUpgrade] Filtering out subscription with no amount:', sub.id);
          return false;
        }
        return true;
      }); // Only include subscriptions with valid amounts
  };

  /**
   * Generate preview for bulk upgrade
   */
  const generatePreview = async (subscriptions: SelectedSubscriptionForUpgrade[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const request: BulkUpgradeRequest = {
        subscriptions: subscriptions.map(sub => ({
          stripe_subscription_id: sub.stripe_subscription_id,
          stripe_customer_id: sub.stripe_customer_id || '',
          current_price_id: sub.metadata?.subscription_record?.stripe_price_id,
          current_amount: sub.current_amount,
          subscription_interval: sub.subscription_interval || 'month',
          //next_billing_date: sub.current_period_end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Default to 30 days if not available
          next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from today
          customer_email: sub.customer_email,
          metadata: sub.metadata
        }))
      };

      const previewData = await subscriptionUpgradeService.previewBulkUpgrade(request);
      setPreview(previewData);
      setIsPreviewModalOpen(true);
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate preview';
      setError(errorMessage);
      
      // Show error toast for preview failure
      toast({
        title: "Preview Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      console.error('[BulkUpgrade] Preview error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Execute bulk upgrade
   */
  const executeBulkUpgrade = async (subscriptions: SelectedSubscriptionForUpgrade[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const request: BulkUpgradeRequest = {
        subscriptions: subscriptions.map(sub => ({
          stripe_subscription_id: sub.stripe_subscription_id,
          stripe_customer_id: sub.stripe_customer_id || '',
          current_price_id: sub.metadata?.subscription_record?.stripe_price_id,
          current_amount: sub.current_amount,
          subscription_interval: sub.subscription_interval || 'month',
          //next_billing_date: sub.current_period_end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from today
          customer_email: sub.customer_email,
          metadata: sub.metadata
        }))
      };

      const result = await subscriptionUpgradeService.executeBulkUpgrade(request);
      
      // Show success toast
      toast({
        title: "Emails Sent Successfully",
        description: `Successfully sent upgrade notification emails to ${result.length} customers. They have been notified about the upcoming changes.`,
        variant: "default",
      });
      
      // Clear selection after successful execution
      clearSelection();
      setIsPreviewModalOpen(false);
      setPreview(null);
      
      return result;
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to execute bulk upgrade';
      setError(errorMessage);
      
      // Show error toast
      toast({
        title: "Bulk Upgrade Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      console.error('[BulkUpgrade] Execution error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle the full bulk upgrade flow
   */
  const handleBulkUpgrade = async (
    subscriptions: SubscriptionRecord[], 
    allStripeData: any[]
  ) => {
    console.log('[BulkUpgrade] Starting bulk upgrade flow');
    console.log('[BulkUpgrade] Selected subscriptions count:', selectedSubscriptions.size);
    console.log('[BulkUpgrade] Total subscriptions available:', subscriptions.length);
    console.log('[BulkUpgrade] Total Stripe data entries:', allStripeData.length);
    
    // Expose data globally for debugging
    (window as any).debugStripeData = allStripeData;
    (window as any).debugSubscriptions = subscriptions;
    (window as any).debugSelectedIds = Array.from(selectedSubscriptions);
    
    if (selectedSubscriptions.size === 0) {
      setError('No subscriptions selected');
      return;
    }

    try {
      const preparedSubscriptions = prepareSubscriptionsForUpgrade(subscriptions, allStripeData);
      
      console.log('[BulkUpgrade] Prepared subscriptions count:', preparedSubscriptions.length);
      console.log('[BulkUpgrade] Prepared subscriptions details:', preparedSubscriptions.map(s => ({
        id: s.id,
        amount: s.current_amount,
        email: s.customer_email
      })));
      
      if (preparedSubscriptions.length === 0) {
        const selectedSubs = subscriptions.filter(sub => selectedSubscriptions.has(sub.id));
        console.error('[BulkUpgrade] No valid subscriptions found. Selected subscriptions:', selectedSubs);
        console.error('[BulkUpgrade] Stripe data sample:', allStripeData.slice(0, 2));
        
        // Provide detailed debugging info
        const debugInfo = selectedSubs.map(sub => {
          const stripeData = allStripeData.find(stripe => 
            stripe.id === sub.stripe_subscription_id || 
            stripe.id === sub.id
          );
          return {
            subscription_id: sub.id,
            stripe_subscription_id: sub.stripe_subscription_id,
            found_stripe_data: !!stripeData,
            stripe_data_structure: stripeData ? {
              has_items: !!stripeData.items,
              items_type: Array.isArray(stripeData.items) ? 'array' : typeof stripeData.items,
              items_length: Array.isArray(stripeData.items) ? stripeData.items.length : 'N/A',
              first_item_price: stripeData.items?.[0]?.price?.unit_amount || 'MISSING'
            } : 'NO_STRIPE_DATA'
          };
        });
        
        console.error('[BulkUpgrade] Debug info for failed subscriptions:', debugInfo);
        
        setError(`No valid subscriptions found for upgrade. Selected ${selectedSubscriptions.size} subscriptions but could not extract pricing data. Check browser console for detailed debugging info.`);
        return;
      }

      await generatePreview(preparedSubscriptions);
      
    } catch (err: any) {
      setError(err.message || 'Failed to prepare bulk upgrade');
      console.error('[BulkUpgrade] Preparation error:', err);
    }
  };

  /**
   * Confirm and execute the upgrade after preview
   */
  const confirmUpgrade = async (selectedTemplate: string = 'professional') => {
    if (!preview) {
      setError('No preview data available');
      return;
    }

    try {
      // Convert preview back to subscription format for execution
      const subscriptionsForExecution = preview.subscriptions.map(previewSub => ({
        id: previewSub.stripe_subscription_id,
        stripe_subscription_id: previewSub.stripe_subscription_id,
        stripe_customer_id: '', // This should be available in the original metadata
        customer_email: previewSub.customer_email,
        current_amount: previewSub.current_amount,
        current_period_end: previewSub.grace_period_end,
        metadata: { template: selectedTemplate }
      }));

      await executeBulkUpgrade(subscriptionsForExecution);
      
    } catch (err: any) {
      console.error('[BulkUpgrade] Confirmation error:', err);
      throw err;
    }
  };

  return {
    selectedSubscriptions,
    isPreviewModalOpen,
    isLoading,
    preview,
    error,
    
    // Actions
    toggleSubscriptionSelection,
    selectAllSubscriptions,
    clearSelection,
    handleBulkUpgrade,
    confirmUpgrade,
    setIsPreviewModalOpen,
    setError,
    
    // Helpers
    prepareSubscriptionsForUpgrade,
    extractSubscriptionAmount,
  };
};
